import { useEffect, useMemo, useState } from "react";
// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-table components
import React from "react";

// @mui material components
import TableContainer from "@mui/material/TableContainer";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";

// Material Dashboard 2 PRO React examples
import { Skeleton, Stack } from "@mui/material";
import MDButton from "components/MDButton";
import { handleShiftKeySelection } from "utils/rowSelection";
import { TableData } from "./TableData";
import { VirtualizedTableData } from "./VirtualizedTableData";
import MDTypography from "components/MDTypography";

function VirtualizedTable({
  canSearch,
  canRefresh,
  canShowRecords,
  searchFun,
  table,
  isSorted,
  onRowClick,
  loader = false,
  isSelection,
  isMultiSelect = true,
  selectedRows,
  setSelectedRows,
  tableHeight = "calc(100vh - 220px)",
  onRowRedirect,
  selectedAll,
  setSelectedAll
}) {
  const [initialRowIndex, setInitialRowIndex] = useState(null);
  const [currentRowIndex, setCurrentRowIndex] = useState(null);
  const [search, setSearch] = useState("");

  const memoizedRows = useMemo(() => table.rows, [table.rows]);
  const memoizedColumns = useMemo(() => table.columns, [table.columns]);

  const extractText = (element) => {
    if (!element) return "";
    if (typeof element === "string") return element;
    if (Array.isArray(element)) return element.map(extractText).join(" ");
    if (React.isValidElement(element)) return extractText(element.props.children);
    return String(element);
  };
  
  const filteredRows = useMemo(() => {
    if (!search) return memoizedRows;
  
    const lowerSearch = search.toLowerCase();
  
    return memoizedRows.filter((row) =>
      memoizedColumns.some(({ accessor }) => {
        const cellValue = extractText(row[accessor]);
        return cellValue.toLowerCase().includes(lowerSearch);
      })
    );
  }, [search, memoizedRows, memoizedColumns]);

  useEffect(() => {
    const selectRowHandler = (index) => {
      const rowData = memoizedRows[index]?.id;
      setSelectedRows((prevSelectedRows) => {
        const updatedRows = new Set(prevSelectedRows);
        updatedRows.has(rowData) ? updatedRows.delete(rowData) : updatedRows.add(rowData);
        return Array.from(updatedRows);
      });
    };

    const handleKeyDown = (event) => {
      handleShiftKeySelection({
        event,
        currentRowIndex,
        initialRowIndex,
        pageLength: memoizedRows.length,
        selectRowHandler,
        setCurrentRowIndex,
      });
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentRowIndex, memoizedRows]);

  return (
    <TableContainer
      sx={{
        height: "100%",
        maxHeight: tableHeight,
        display: "flex",
        flexDirection: "column",
        boxShadow: "none",
      }}
    >
      {/* Header */}
      {(canSearch || canRefresh || canShowRecords) && (
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          pl={3}
          pr={3}
          pt={1}
          pb={1}
          sx={{
            flexShrink: 0,
            backgroundColor: "white",
            position: "sticky",
            flexWrap: "wrap",
            gap: 1,
            top: 0,
            zIndex: 2,
          }}
        >
          <MDBox display="flex" alignItems="center" sx={{ mr: "auto", flexWrap: "nowrap" }}>
            {canShowRecords && (
              <>
                <MDTypography variant="body2" sx={{ fontWeight: 'bold' }}>
                  Selected Records : 
                  <span style={{ fontWeight: 'normal' }}> {selectedRows.length} / {memoizedRows.length}</span>
                </MDTypography>
                {/* <MDTypography variant="body2" sx={{ fontWeight: 'bold' }}>
                  Failed Records : 
                  <span style={{ fontWeight: 'normal' }}> {failedRow?.length} / {selectedRows.length}</span>
                </MDTypography>
                <MDTypography variant="body2" sx={{ fontWeight: 'bold' }}>
                  Passed Records : 
                  <span style={{ fontWeight: 'normal' }}> {passedRow?.length} / {selectedRows.length}</span>
                </MDTypography> */}
              </>
            )}
          </MDBox>

          <MDBox display="flex" alignItems="center" sx={{ ml: "auto", flexWrap: "nowrap" }}>
            {canRefresh && (
              <MDBox width="4rem" ml="auto" style={{ marginRight: "10px" }}>
                <MDButton
                  size="small"
                  variant="gradient"
                  color="dark"
                  fullWidth
                  onClick={() => (!loader ? searchFun({ preserveSelection: true }) : {})}
                >
                  <i className={"fas fa-refresh"}></i>
                </MDButton>
              </MDBox>
            )}
            {canSearch && (
              <MDBox width="12rem" ml="auto">
                <MDInput
                  placeholder="Search..."
                  value={search}
                  size="small"
                  fullWidth
                  onChange={({ currentTarget }) => {
                    setSearch(currentTarget.value);
                  }}
                />
              </MDBox>
            )}
          </MDBox>
        </MDBox>
      )}

      {/* Scrollable Table Data */}
      <MDBox
        sx={{
          flexGrow: 1,
          overflow: "auto",
          height: "100vh"
        }}
      >
        {!loader ? (
          // <TableData
          //   initialRowIndex={initialRowIndex}
          //   isSelection={isSelection}
          //   isMultiSelect={isMultiSelect}
          //   selectedAll={selectedAll}
          //   selectedRows={selectedRows}
          //   setSelectedRows={setSelectedRows}
          //   onRowClick={onRowClick}
          //   onRowRedirect={onRowRedirect}
          //   setSelectedAll={setSelectedAll}
          //   memoizedRows={memoizedRows}
          //   memoizedColumns={memoizedColumns}
          //   setInitialRowIndex={setInitialRowIndex}
          //   setCurrentRowIndex={setCurrentRowIndex}
          // />
          <VirtualizedTableData
            initialRowIndex={initialRowIndex}
            isSelection={isSelection}
            isMultiSelect={isMultiSelect}
            selectedAll={selectedAll}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            onRowClick={onRowClick}
            onRowRedirect={onRowRedirect}
            setSelectedAll={setSelectedAll}
            memoizedRows={filteredRows}
            memoizedColumns={memoizedColumns}
            setInitialRowIndex={setInitialRowIndex}
            setCurrentRowIndex={setCurrentRowIndex}
          />
        ) : (
          <StackSkeleton />
        )}
      </MDBox>
    </TableContainer>
  );
}

// Setting default values for the props of DataTable
VirtualizedTable.defaultProps = {
  canSearch: false,
  isSorted: true,
  noEndBorder: false,
  deepSearch: false,
  fixedWidth: false,
  onRowClick: () => {},
};

// Typechecking props for the DataTable
VirtualizedTable.propTypes = {
  canRefresh: PropTypes.bool,
  canSearch: PropTypes.bool,
  table: PropTypes.objectOf(PropTypes.array).isRequired,
  searchFun: PropTypes.func,
  isSorted: PropTypes.bool,
  noEndBorder: PropTypes.bool,
  onRowClick: PropTypes.func,
  deepSearch: PropTypes.bool,
  fixedWidth: PropTypes.bool,
};

function StackSkeleton() {
  return (
    <Stack spacing={0.5}>
      <Skeleton variant="rectangular" sx={{ fontSize: "4rem" }} />
      <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
      <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
      <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
      <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
      <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
    </Stack>
  );
}

export default React.memo(VirtualizedTable);
